import React, { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import type { DetailedTariffItem } from '../../../apis/types'
import { updateTariffApi } from '../../../apis/apis'

const inputClass =
    'block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100'

type FieldErrors = {
    schedule?: string
    name?: string
    reason?: string
}

export const EditTariffModal: React.FC<{
    open: boolean
    onClose: () => void
    tariff: DetailedTariffItem
    onSaved: (tariff: DetailedTariffItem) => void
}> = ({ open, onClose, tariff, onSaved }) => {
    const [schedule, setSchedule] = useState(tariff.schedule)
    const [name, setName] = useState(tariff.name ?? '')
    const [internalNotes, setInternalNotes] = useState(tariff.modifiedDescription ?? '')
    const [reason, setReason] = useState('')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

    useEffect(() => {
        if (!open) return
        setSchedule(tariff.schedule)
        setName(tariff.name ?? '')
        setInternalNotes(tariff.modifiedDescription ?? '')
        setReason('')
        setError(null)
        setFieldErrors({})
    }, [open, tariff])

    const validate = (): boolean => {
        const next: FieldErrors = {}
        if (schedule.trim() === '') next.schedule = 'Schedule is required.'
        if (reason.trim() === '') next.reason = 'Reason is required.'
        setFieldErrors(next)
        return Object.keys(next).length === 0
    }

    const handleSave = (): void => {
        if (!validate()) return
        setSaving(true)
        setError(null)
        const nextNotes = internalNotes.trim() === '' ? null : internalNotes.trim()
        const previousNotes =
            tariff.modifiedDescription == null || tariff.modifiedDescription.trim() === ''
                ? null
                : tariff.modifiedDescription.trim()
        void updateTariffApi(tariff.cuid, {
            schedule: schedule.trim(),
            name: name.trim() === '' ? null : name.trim(),
            ...(nextNotes !== previousNotes ? { modifiedDescription: nextNotes } : {}),
            reason: reason.trim(),
        })
            .then((updated) => {
                onSaved(updated)
                onClose()
            })
            .catch(() => setError('Failed to save tariff.'))
            .finally(() => setSaving(false))
    }

    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-5 text-left shadow-xl dark:bg-slate-900">
                                <Dialog.Title className="text-lg font-medium text-slate-900 dark:text-slate-100">
                                    Edit tariff
                                </Dialog.Title>
                                <div className="mt-4 space-y-3">
                                    <div>
                                        <label
                                            htmlFor="edit-tariff-schedule"
                                            className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
                                        >
                                            Schedule
                                        </label>
                                        <input
                                            id="edit-tariff-schedule"
                                            className={inputClass}
                                            value={schedule}
                                            onChange={(e) => setSchedule(e.target.value)}
                                        />
                                        {fieldErrors.schedule != null ? (
                                            <p className="mt-1 text-xs text-red-600">{fieldErrors.schedule}</p>
                                        ) : null}
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="edit-tariff-name"
                                            className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
                                        >
                                            Name
                                        </label>
                                        <input
                                            id="edit-tariff-name"
                                            className={inputClass}
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="edit-tariff-notes"
                                            className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
                                        >
                                            Internal notes
                                        </label>
                                        <textarea
                                            id="edit-tariff-notes"
                                            className={inputClass}
                                            rows={3}
                                            value={internalNotes}
                                            onChange={(e) => setInternalNotes(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="edit-tariff-reason"
                                            className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
                                        >
                                            Reason{' '}
                                            <span className="font-normal text-slate-500">(required)</span>
                                        </label>
                                        <textarea
                                            id="edit-tariff-reason"
                                            className={inputClass}
                                            rows={3}
                                            maxLength={500}
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                        />
                                        {fieldErrors.reason != null ? (
                                            <p className="mt-1 text-xs text-red-600">{fieldErrors.reason}</p>
                                        ) : null}
                                    </div>
                                    {error != null ? (
                                        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                                    ) : null}
                                </div>
                                <div className="mt-5 flex justify-end gap-2">
                                    <button
                                        type="button"
                                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                                        onClick={onClose}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
                                        onClick={handleSave}
                                        disabled={saving || reason.trim() === ''}
                                    >
                                        {saving ? 'Saving…' : 'Save'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default EditTariffModal
